import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import confetti from 'canvas-confetti';
import { PartyPopper, X, ArrowRight, Play } from 'lucide-react';
import { welcomeApi, files as filesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ytId = (u) => (u || '').match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/)?.[1];

const burst = () => {
  const colors = ['#16a34a', '#facc15', '#ffffff', '#22c55e', '#000000'];
  confetti({ particleCount: 140, spread: 90, origin: { y: 0.35 }, colors, zIndex: 10000 });
  setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 70, origin: { x: 0, y: 0.6 }, colors, zIndex: 10000 }), 250);
  setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 70, origin: { x: 1, y: 0.6 }, colors, zIndex: 10000 }), 400);
};

const Media = ({ v }) => {
  const id = ytId(v.videoUrl);
  if (id) return <iframe title="Welcome video" className="w-full aspect-video" src={`https://www.youtube.com/embed/${id}`} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen data-testid="welcome-video"/>;
  if (v.videoUrl) return <video className="w-full aspect-video bg-black" src={v.videoUrl} controls poster={v.imageUrl ? filesApi.urlFor(v.imageUrl) : undefined} data-testid="welcome-video"/>;
  return v.imageUrl ? <img src={filesApi.urlFor(v.imageUrl)} alt="" className="w-full aspect-[3/2] object-cover" data-testid="welcome-image"/> : null;
};

export default function WelcomeModal() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const variant = user?.isBusinessOwner ? 'owner' : 'consumer';

  useEffect(() => {
    if (!user?.welcomePending) return;
    welcomeApi.get().then(c => { setCfg(c[variant]); setOpen(true); setTimeout(burst, 150); }).catch(() => {});
  }, [user?.welcomePending, variant]);

  const close = async (path) => {
    setOpen(false);
    try { sessionStorage.setItem('pbb_welcome_shown', '1'); } catch {}
    try { await welcomeApi.dismiss(); await refresh?.(); } catch {}
    navigate(path || '/');
  };

  if (!open || !cfg) return null;
  return (
    <div className="fixed inset-0 z-[9999] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4" data-testid={`welcome-modal-${variant}`}>
      <div className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-2xl bg-[#0b0b0b] border border-green-600/40 shadow-2xl animate-in fade-in zoom-in-95 duration-300">
        <button onClick={() => close()} aria-label="Close" data-testid="welcome-close"
          className="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black"><X size={16}/></button>
        <Media v={cfg}/>
        <div className="p-6">
          <div className="text-[11px] uppercase tracking-widest text-yellow-400 font-semibold inline-flex items-center gap-1.5"><PartyPopper size={13}/> {variant === 'owner' ? 'Member + Business Owner' : 'New Member'}</div>
          <h2 className="mt-1 text-2xl font-bold text-white" data-testid="welcome-title">{cfg.title}</h2>
          <p className="mt-3 text-sm text-gray-200 leading-relaxed whitespace-pre-line" data-testid="welcome-body">{cfg.body}</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            {cfg.ctaLabel && cfg.ctaPath && (
              <button onClick={() => close(cfg.ctaPath)} data-testid="welcome-cta"
                className={`flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm ${variant === 'owner' ? 'bg-yellow-400 hover:bg-yellow-300 text-black' : 'bg-green-600 hover:bg-green-500 text-white'}`}>
                {variant === 'owner' ? <Play size={14}/> : null}{cfg.ctaLabel} <ArrowRight size={14}/>
              </button>
            )}
            <button onClick={() => close()} data-testid="welcome-dismiss"
              className="px-5 py-3 rounded-full border border-[#2a2a2a] text-gray-200 hover:border-green-600/60 text-sm">
              {variant === 'owner' ? 'Go to my dashboard' : "Let's go!"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
